import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Youtube, Mail } from 'lucide-react';
import { footerData } from '../data/mock';
import { useT } from '../i18n';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useT();

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="text-2xl font-bold text-teal-400 mb-4 block">
              LearnixTa
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed" data-testid="footer-tagline">
              {t('footer.tagline')}
            </p>
            <div className="flex space-x-4">
              {[
                { label: 'Instagram', Icon: Instagram, url: 'https://www.instagram.com/learnixta_/', aria: 'LearnixTa on Instagram' },
                { label: 'LinkedIn', Icon: Linkedin, url: 'https://www.linkedin.com/company/learnixta' },
                { label: 'YouTube', Icon: Youtube, url: 'https://www.youtube.com/@learnixta' },
              ].filter((s) => s.url).map(({ label, Icon, url, aria }) => (
                <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 hover:scale-110 transition-[background-color,transform]"
                  aria-label={aria || label} title={aria || label} data-testid={`footer-social-${label.toLowerCase()}`}>
                  <Icon size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Pathways */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t('nav.pathways')}</h4>
            <ul className="space-y-3">
              {footerData.pathways.map((item, index) => (
                <li key={index}>
                  <Link to={item.link} className="hover:text-teal-400 transition-colors text-sm">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Explore */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t('nav.explore')}</h4>
            <ul className="space-y-3">
              {footerData.explore.map((item, index) => (
                <li key={index}>
                  <Link to={item.link} className="hover:text-teal-400 transition-colors text-sm">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Platform + Partners columns temporarily hidden. */}

          {/* Company */}
          <div>
            <h4 className="font-semibold text-white mb-4">LearnixTa</h4>
            <ul className="space-y-3">
              {footerData.company.map((item, index) => (
                <li key={index}>
                  <Link to={item.link} className="hover:text-teal-400 transition-colors text-sm">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-semibold text-white mb-4 mt-6">Legal</h4>
            <ul className="space-y-3" data-testid="footer-legal-column">
              {footerData.legal.map((item, index) => (
                <li key={index}>
                  <Link to={item.link} className="hover:text-teal-400 transition-colors text-sm">
                    {item.name}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event('lx-open-cookie-banner'))}
                  className="hover:text-teal-400 transition-colors text-sm"
                  data-testid="footer-cookie-preferences"
                >
                  Cookie preferences
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        {false && <div className="border-t border-gray-800 pt-8 mb-8">
          <div className="max-w-md mx-auto text-center">
            <h4 className="font-semibold text-white mb-2">{t('footer.stay_updated')}</h4>
            <p className="text-gray-400 text-sm mb-4">{t('footer.stay_updated_sub')}</p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-full text-gray-300 focus:outline-none focus:border-teal-500 text-sm"
              />
              <button className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-medium text-sm transition-colors">
                <Mail size={18} />
              </button>
            </div>
          </div>
        </div>

        }
        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-500">
          <p>© {currentYear} <Link to="/copyright" className="hover:text-teal-400 transition-colors" data-testid="footer-copyright-link">LearnixTa</Link>. All rights reserved.</p>
          <p className="mt-2 text-xs max-w-3xl mx-auto" data-testid="footer-ai-note">LearnixTa uses AI to organize and explain career information. AI can make mistakes. Verify important information — such as eligibility, exams, fees, salaries and government schemes — using the official sources linked on each page. LearnixTa does not guarantee admission, employment or income.</p>
          <p className="mt-2">{t('footer.made_with')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
